import { useMemo } from "react";
import { Route, Routes, useSearchParams } from "react-router";
import { withAuthenticationRequired } from "react-oidc-context";
import { ChangePin } from "../ChangePin/ChangePin";
import { EventList } from "../Events/EventList/EventList";
import { getAuthenticationContextReference } from "~/helpers/getAuthenticationContextReference";
import { Home } from "../Home/Home";
import { Login } from "~/components/Login/Login";
import { MainLayout } from "../Layout/MainLayout";
import { NotFound } from "../NotFound/NotFound";
import { Pdf } from "../Pdf/Pdf";
import { PreviewForm } from "../Form/PreviewForm/PreviewForm";
import { ProtectedRoute } from "~/components/Base/ProtectedRoute/ProtectedRoute";
import { PublicLayout } from "../Layout/PublicLayout";
import { RedirectPage } from "../RedirectPage/RedirectPage";
import { Settings } from "../Settings/Settings";
import { SigninCallback } from "~/components/SigninCallback/SigninCallback";
import { StudyInfo } from "../StudyInfo/StudyInfo";
import { UnscheduledEventList } from "../Events/UnscheduledEventList/UnscheduledEventList";
import { ValidatePageIdForm } from "../ValidatePageIdForm/ValidatePageIdForm";
import { Welcome } from "../Welcome/Welcome";
import { isEmbedded } from "~/helpers/isEmbedded";

export const ApplicationRoutes = () => {
  const [searchParams] = useSearchParams();
  const loginHint = searchParams.get("login_hint");
  const acrValues = getAuthenticationContextReference(
    searchParams.get("authm"),
    searchParams.get("locale")
  );

  const RequireAuth = useMemo(() => {
    const signinRedirectArgs = {
      ...(loginHint && { login_hint: loginHint }),
      ...(acrValues && { acr_values: acrValues }),
    };

    return withAuthenticationRequired(ProtectedRoute, {
      signinRedirectArgs,
      OnRedirecting: () => <RedirectPage />,
    });
  }, [loginHint, acrValues]);

  if (isEmbedded()) {
    /*
      A framed document must only ever show /preview. nginx's
      X-Frame-Options: DENY on location / applies per document fetch, so it
      cannot stop client-side navigation happening inside an
      allowed-ancestor /preview iframe. Restricting the route table makes
      "framed = preview only" structural.
    */
    return (
      <Routes>
        <Route path="/preview" element={<PreviewForm />} />
        <Route path="*" element={null} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/signin-oidc" element={<SigninCallback />} />
      <Route path="/login" element={<Login />} />
      <Route path="/pdf/:formId" element={<Pdf />} />
      {/*
        /preview is mounted OUTSIDE RequireAuth — the Designer iframe holds
        no patient OIDC session. QuestModel arrives only via postMessage
        from the parent; the route makes no API calls. See
        docs/features/2-preview-route.md.
      */}
      <Route path="/preview" element={<PreviewForm />} />
      <Route element={<PublicLayout />}>
        <Route element={<RequireAuth />}>
          <Route path="/change-pin" element={<ChangePin />} />
          <Route path="/welcome" element={<Welcome />} />
        </Route>
      </Route>
      <Route element={<RequireAuth />}>
        <Route path="/" element={<MainLayout />}>
          <Route index element={<Home />} />
          <Route path="study" element={<StudyInfo />} />
          <Route path="settings" element={<Settings />} />
          <Route path="events">
            <Route index element={<EventList />} />
            <Route
              path=":eventId/form/:page"
              element={<ValidatePageIdForm />}
            />
          </Route>
          <Route path="unscheduled-events">
            <Route index element={<UnscheduledEventList />} />
            <Route
              path=":eventId/form/:page"
              element={<ValidatePageIdForm />}
            />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
};
